/* Admin → Landing Page editor (iter-29).
 *
 * Side-by-side editor for the public `/` route. Three tabs on the left
 * (Content / Theme / SEO), a live preview on the right that mounts the
 * same `LandingView` component the live page uses — so what the admin
 * sees here matches what visitors get the second they hit Save.
 *
 * Editable surfaces:
 *   - Content: every label, eyebrow, headline, body, footer copy.
 *   - Theme:   six web-safe font stacks, 13-20 px base size, four
 *               colors (page bg, card bg, accent, accent text).
 *   - SEO:     title, description, keywords, Open Graph image/title/
 *               description + a deterministic 0-100 score with green/
 *               yellow/red signals and a "Suggest with AI" button.
 *
 * Button positions/functions in the live page are NOT configurable —
 * only their text. (Per feature spec.)
 */
import React, { useEffect, useMemo, useState, useCallback } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs';
import {
  AlertDialog, AlertDialogTrigger, AlertDialogContent, AlertDialogHeader,
  AlertDialogTitle, AlertDialogDescription, AlertDialogFooter,
  AlertDialogCancel, AlertDialogAction,
} from '@/components/ui/alert-dialog';
import {
  Save, RotateCcw, Sparkles, Loader2, Type, Palette, Search,
  Check, AlertTriangle, X, Wand2, Eye, Flag,
} from 'lucide-react';
import { toast } from 'sonner';
import { landingAdminApi } from '@/lib/landingApi';
import { mediaDevelopmentApi } from '@/lib/api';
import { FONT_LABELS } from '@/components/landing/landingDefaults';
import LandingView from '@/components/landing/LandingView';
import { PanelHeader, Field } from './_shared';


// iter55u — CONTENT_GROUPS + FONT_KEYS + Severity + ScoreRing moved to a sibling file.
import { CONTENT_GROUPS, FONT_KEYS, Severity, ScoreRing } from "./LandingPanelSchema";
import { LanguageFlagManager } from './LanguageFlagManager';
import LandingLocalizationPanel from '@/components/admin/LandingLocalizationPanel';
export default function LandingPanel() {
  const [serverDoc, setServerDoc] = useState(null);  // last-saved version
  const [draft, setDraft] = useState(null);          // dirty editing copy
  const [defaults, setDefaults] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [scoring, setScoring] = useState(false);
  const [scoreData, setScoreData] = useState(null);
  const [suggesting, setSuggesting] = useState(false);
  // Feb-2026 — "Admin Media Linking": quick-pick the Mei intro video/poster
  // straight from Media Center instead of hand-typing a URL.
  const [mediaItems, setMediaItems] = useState([]);
  useEffect(() => {
    mediaDevelopmentApi.list()
      .then((r) => setMediaItems(r.items || []))
      .catch((e) => console.warn('[landing-admin] Media Center picker: could not load items (manual URL entry still works)', e?.response?.status || e));
  }, []);

  const reload = useCallback(async () => {
    setLoading(true);
    try {
      const r = await landingAdminApi.get();
      setServerDoc({ content: r.content, theme: r.theme, seo: r.seo });
      setDraft({ content: r.content, theme: r.theme, seo: r.seo });
      setDefaults(r.defaults);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Could not load landing-page config.');
    } finally { setLoading(false); }
  }, []);

  useEffect(() => { reload(); }, [reload]);

  // Score the SEO tab as the admin types (debounced).
  useEffect(() => {
    if (!draft?.seo) return;
    setScoring(true);
    const handle = setTimeout(async () => {
      try {
        const r = await landingAdminApi.seoScore(draft.seo);
        setScoreData(r);
      } catch (e) {
        console.debug('[seo-score] transient failure — will retry on next change:', e?.message);
      } finally { setScoring(false); }
    }, 400);
    return () => clearTimeout(handle);
  }, [draft?.seo]);

  const dirty = useMemo(() => {
    if (!serverDoc || !draft) return false;
    return JSON.stringify(serverDoc) !== JSON.stringify(draft);
  }, [serverDoc, draft]);

  const setContent = (k, v) => setDraft((d) => ({ ...d, content: { ...d.content, [k]: v } }));
  const setTheme   = (k, v) => setDraft((d) => ({ ...d, theme:   { ...d.theme,   [k]: v } }));
  const setSeo     = (k, v) => setDraft((d) => ({ ...d, seo:     { ...d.seo,     [k]: v } }));

  const save = async () => {
    setBusy(true);
    try {
      const r = await landingAdminApi.save({
        content: draft.content,
        theme:   draft.theme,
        seo:     draft.seo,
      });
      setServerDoc({ content: r.content, theme: r.theme, seo: r.seo });
      setDraft({ content: r.content, theme: r.theme, seo: r.seo });
      toast.success('Landing page saved. Live for visitors immediately.');
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Save failed.');
    } finally { setBusy(false); }
  };

  const reset = async () => {
    setBusy(true);
    try {
      const r = await landingAdminApi.reset();
      setServerDoc({ content: r.content, theme: r.theme, seo: r.seo });
      setDraft({ content: r.content, theme: r.theme, seo: r.seo });
      toast.success('Defaults restored.');
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Reset failed.');
    } finally { setBusy(false); }
  };

  const suggestSeo = async () => {
    setSuggesting(true);
    try {
      const r = await landingAdminApi.seoSuggest({});
      const sug = r.suggestion || {};
      setSeo('title',       sug.title       || draft.seo.title);
      setSeo('description', sug.description || draft.seo.description);
      setSeo('keywords',    sug.keywords    || draft.seo.keywords);
      toast.success('AI suggestion pasted into the draft. Review then Save.');
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Could not get an AI suggestion.');
    } finally { setSuggesting(false); }
  };

  if (loading || !draft) {
    return (
      <section>
        <PanelHeader title="Landing Page" />
        <div className="mt-4 flex items-center gap-2 text-[12px] text-muted-foreground">
          <Loader2 className="h-3.5 w-3.5 animate-spin" /> Loading…
        </div>
      </section>
    );
  }

  return (
    <section data-testid="admin-landing-panel">
      <PanelHeader
        title="Landing Page"
        subtitle="Edit every label, color, font, and SEO field on the public / route. Button positions and click-targets are locked; only copy and styling are editable."
        onRefresh={reload}
      />

      <div className="mt-3 mb-4 flex flex-wrap items-center gap-2">
        <Button size="sm" onClick={save} disabled={!dirty || busy}
                 className="gap-1.5" data-testid="landing-save">
          {busy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Save className="h-3.5 w-3.5" />}
          {dirty ? 'Save changes' : 'No changes'}
        </Button>
        <AlertDialog>
          <AlertDialogTrigger asChild>
            <Button size="sm" variant="outline" disabled={busy}
                     className="gap-1.5" data-testid="landing-reset">
              <RotateCcw className="h-3.5 w-3.5" /> Reset to defaults
            </Button>
          </AlertDialogTrigger>
          <AlertDialogContent data-testid="landing-reset-confirm">
            <AlertDialogHeader>
              <AlertDialogTitle>Restore landing-page defaults?</AlertDialogTitle>
              <AlertDialogDescription>
                This wipes every override — content, theme, SEO — and restores the built-in defaults.
                The public <span className="font-mono-stat">/</span> route updates immediately for all visitors.
                This action cannot be undone.
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel data-testid="landing-reset-cancel">Cancel</AlertDialogCancel>
              <AlertDialogAction onClick={reset} data-testid="landing-reset-confirm-btn">
                Reset to defaults
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
        {dirty && (
          <span className="text-[11px] text-amber-600">
            Unsaved changes — preview reflects your draft only.
          </span>
        )}
      </div>

      <div className="grid grid-cols-1 gap-6 xl:grid-cols-[minmax(0,1fr)_minmax(0,1fr)]">
        {/* LEFT — editor */}
        <div className="min-w-0">
          <Tabs defaultValue="content">
            <TabsList className="mb-3">
              <TabsTrigger value="content" data-testid="landing-tab-content"   className="gap-1.5 text-[12px]">
                <Type className="h-3.5 w-3.5" /> Content
              </TabsTrigger>
              <TabsTrigger value="theme"   data-testid="landing-tab-theme"     className="gap-1.5 text-[12px]">
                <Palette className="h-3.5 w-3.5" /> Theme
              </TabsTrigger>
              <TabsTrigger value="seo"     data-testid="landing-tab-seo"       className="gap-1.5 text-[12px]">
                <Search className="h-3.5 w-3.5" /> SEO
              </TabsTrigger>
              <TabsTrigger value="flags" data-testid="landing-tab-flags" className="gap-1.5 text-[12px]">
                <Flag className="h-3.5 w-3.5" /> Flags
              </TabsTrigger>
            </TabsList>

            {/* Content tab */}
            <TabsContent value="content" className="m-0 space-y-4">
              {CONTENT_GROUPS.map((g) => (
                <div key={g.title} className="rounded-md border border-border bg-card p-4">
                  <div className="mb-3 text-[12.5px] font-semibold">{g.title}</div>
                  <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
                    {g.fields.map(([k, label, kind]) => (
                      <Field key={k} label={label}
                              className={kind === 'textarea' ? 'md:col-span-2' : undefined}>
                        {kind === 'textarea' ? (
                          <textarea
                            value={draft.content[k] || ''}
                            onChange={(e) => setContent(k, e.target.value)}
                            rows={3}
                            data-testid={`landing-content-${k}`}
                            className="w-full rounded-md border border-border bg-background px-3 py-2 text-[12.5px] outline-none focus:border-primary"
                          />
                        ) : (
                          <Input
                            value={draft.content[k] || ''}
                            onChange={(e) => setContent(k, e.target.value)}
                            data-testid={`landing-content-${k}`}
                            className="h-8 text-[12.5px]"
                          />
                        )}
                        {(kind === 'media-link-video' || kind === 'media-link-poster') && (
                          <>
                            <select
                              defaultValue=""
                              data-testid={`landing-content-${k}-media-picker`}
                              onChange={(e) => {
                                if (e.target.value) setContent(k, e.target.value);
                                e.target.value = '';
                              }}
                              className="mt-1.5 h-7 w-full rounded-md border border-dashed border-border bg-secondary/30 px-2 text-[11px] text-muted-foreground outline-none focus:border-primary"
                            >
                              <option value="">— or quick-pick from Media Center —</option>
                              {mediaItems
                                // Only "published + published_to_landing" items are reachable by
                                // anonymous visitors (via /api/public/marketing-media/...) — the
                                // admin file URL on `m.url` is auth-gated and would 403 on the
                                // public landing page.
                                .filter((m) => m.status === 'published' && m.published_to_landing
                                  && (kind === 'media-link-video' ? m.media_kind === 'video'
                                                                   : !!(m.poster_url || m.thumbnail_url)))
                                .map((m) => (
                                  <option
                                    key={m.id}
                                    value={`/api/public/marketing-media/${m.id}/${kind === 'media-link-video' ? 'primary' : (m.poster_url ? 'poster' : 'thumbnail')}`}
                                  >
                                    {m.title} ({m.slug})
                                  </option>
                                ))}
                            </select>
                            {mediaItems.length > 0 && !mediaItems.some((m) => m.status === 'published' && m.published_to_landing) && (
                              <p className="mt-1 text-[10px] text-muted-foreground">
                                No Media Center items are published + "Landing"-flagged yet — mark one Published with "Publish to landing" in Marketing &amp; Media first.
                              </p>
                            )}
                          </>
                        )}
                      </Field>
                    ))}
                  </div>
                </div>
              ))}
            </TabsContent>

            {/* Theme tab */}
            <TabsContent value="theme" className="m-0 space-y-4">
              <div className="rounded-md border border-border bg-card p-4">
                <div className="mb-3 text-[12.5px] font-semibold">Typography</div>
                <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
                  <Field label="Font family">
                    <select
                      value={draft.theme.font_family_key}
                      onChange={(e) => setTheme('font_family_key', e.target.value)}
                      data-testid="landing-theme-font"
                      className="h-8 w-full rounded-md border border-border bg-background px-2 text-[12.5px] focus:outline-none focus:ring-2 focus:ring-ring"
                    >
                      {FONT_KEYS.map((k) => (
                        <option key={k} value={k}>{FONT_LABELS[k] || k}</option>
                      ))}
                    </select>
                  </Field>
                  <Field label={`Base font size — ${draft.theme.base_font_size_px}px`}>
                    <input
                      type="range" min={13} max={20} step={1}
                      value={draft.theme.base_font_size_px}
                      onChange={(e) => setTheme('base_font_size_px', parseInt(e.target.value, 10))}
                      data-testid="landing-theme-size"
                      className="w-full"
                    />
                  </Field>
                </div>
              </div>

              <div className="rounded-md border border-border bg-card p-4">
                <div className="mb-3 text-[12.5px] font-semibold">Colors</div>
                <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
                  {[
                    ['background_color',         'Page background'],
                    ['card_background_color',    'Section / card background'],
                    ['accent_color',              'Accent (button background)'],
                    ['accent_foreground_color',   'Accent foreground (button text)'],
                  ].map(([k, label]) => (
                    <Field key={k} label={label}>
                      <div className="flex items-center gap-2">
                        <input
                          type="color"
                          value={draft.theme[k]}
                          onChange={(e) => setTheme(k, e.target.value)}
                          data-testid={`landing-color-${k}`}
                          className="h-8 w-12 cursor-pointer rounded border border-border bg-transparent p-0"
                        />
                        <Input
                          value={draft.theme[k]}
                          onChange={(e) => setTheme(k, e.target.value)}
                          className="h-8 font-mono-stat text-[12px]"
                        />
                      </div>
                    </Field>
                  ))}
                </div>
              </div>
            </TabsContent>

            {/* SEO tab */}
            <TabsContent value="seo" className="m-0 space-y-4">
              <div className="rounded-md border border-border bg-card p-4">
                <div className="mb-3 flex items-center justify-between">
                  <div className="text-[12.5px] font-semibold">Page metadata</div>
                  <Button size="sm" variant="outline" onClick={suggestSeo} disabled={suggesting}
                           data-testid="landing-seo-suggest"
                           className="gap-1.5">
                    {suggesting ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Wand2 className="h-3.5 w-3.5" />}
                    Suggest with AI
                  </Button>
                </div>
                <div className="grid grid-cols-1 gap-3">
                  <Field label={`Title — ${(draft.seo.title || '').length} chars (30-60 optimal)`}>
                    <Input
                      value={draft.seo.title || ''}
                      onChange={(e) => setSeo('title', e.target.value)}
                      data-testid="landing-seo-title"
                      className="h-8 text-[12.5px]"
                    />
                  </Field>
                  <Field label={`Description — ${(draft.seo.description || '').length} chars (120-160 optimal)`}>
                    <textarea
                      value={draft.seo.description || ''}
                      onChange={(e) => setSeo('description', e.target.value)}
                      rows={3}
                      data-testid="landing-seo-description"
                      className="w-full rounded-md border border-border bg-background px-3 py-2 text-[12.5px] outline-none focus:border-primary"
                    />
                  </Field>
                  <Field label="Keywords (comma-separated, 3-10)">
                    <textarea
                      value={draft.seo.keywords || ''}
                      onChange={(e) => setSeo('keywords', e.target.value)}
                      rows={2}
                      data-testid="landing-seo-keywords"
                      className="w-full rounded-md border border-border bg-background px-3 py-2 text-[12.5px] outline-none focus:border-primary"
                    />
                  </Field>
                  <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
                    <Field label="Open Graph image URL">
                      <Input value={draft.seo.og_image || ''} onChange={(e) => setSeo('og_image', e.target.value)}
                              data-testid="landing-seo-og-image" className="h-8 text-[12.5px]" />
                    </Field>
                    <Field label="og:title override (optional)">
                      <Input value={draft.seo.og_title || ''} onChange={(e) => setSeo('og_title', e.target.value)}
                              data-testid="landing-seo-og-title" className="h-8 text-[12.5px]" />
                    </Field>
                  </div>
                  <Field label="og:description override (optional)">
                    <textarea
                      value={draft.seo.og_description || ''}
                      onChange={(e) => setSeo('og_description', e.target.value)}
                      rows={2}
                      data-testid="landing-seo-og-description"
                      className="w-full rounded-md border border-border bg-background px-3 py-2 text-[12.5px] outline-none focus:border-primary"
                    />
                  </Field>
                </div>
              </div>

              {/* Score + signals */}
              <div className="rounded-md border border-border bg-card p-4" data-testid="landing-seo-score">
                <div className="mb-3 flex items-center gap-4">
                  <ScoreRing score={scoreData?.score ?? 0} />
                  <div>
                    <div className="text-[12.5px] font-semibold flex items-center gap-2">
                      SEO health
                      {scoring && <Loader2 className="h-3 w-3 animate-spin opacity-60" />}
                    </div>
                    <div className="text-[11px] text-muted-foreground">
                      Deterministic checks — title length, description length, keyword count, Open Graph presence.
                    </div>
                  </div>
                </div>
                <ul className="space-y-1.5">
                  {(scoreData?.signals || []).map((s) => (
                    <li key={s.field}
                         data-testid={`landing-seo-signal-${s.field}`}
                         className="flex items-start gap-2 text-[11.5px]">
                      <Severity severity={s.severity} />
                      <span className={
                        s.severity === 'fail' ? 'text-red-500' :
                        s.severity === 'warn' ? 'text-amber-600' :
                        'text-muted-foreground'
                      }>
                        <b className="text-foreground">{s.field}</b> — {s.message}
                        <span className="ml-1 font-mono-stat text-[10.5px] opacity-70">
                          ({s.points}/{s.max_points})
                        </span>
                      </span>
                    </li>
                  ))}
                </ul>
              </div>
            </TabsContent>

            <TabsContent value="flags" className="m-0 space-y-4">
              <div className="rounded-md border border-border bg-card p-4">
                <div className="mb-1 text-[12.5px] font-semibold">Language flags</div>
                <p className="mb-4 text-[11px] text-muted-foreground">
                  Regenerate one locale with Nano Banana. The new image replaces that flag across landing and Bookstore surfaces.
                </p>
                <LanguageFlagManager />
              </div>
              {/* Oct-2026 — Locale regionalization: per-locale AI copy + video */}
              <LandingLocalizationPanel />
            </TabsContent>
          </Tabs>
        </div>

        {/* RIGHT — live preview */}
        <div className="sticky top-2 self-start">
          <div className="mb-2 flex items-center gap-2 text-[11.5px] text-muted-foreground">
            <Eye className="h-3.5 w-3.5" /> Live preview · 50% scale · clicks disabled
          </div>
          <div
            className="overflow-hidden rounded-md border border-border bg-background shadow-panel-active"
            style={{ height: 'clamp(420px, 70vh, 760px)' }}
            data-testid="landing-preview"
          >
            <div
              style={{
                width:           '200%',
                height:          '200%',
                transform:       'scale(0.5)',
                transformOrigin: 'top left',
                overflowY:       'auto',
              }}
            >
              <LandingView
                content={draft.content}
                theme={draft.theme}
                interactive={false}
              />
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
